/**
 * Plans a snapshot restore as a diff against what this tab's WebContainer holds
 * (PROTOTYPER_STABILITY_TECH_PLAN.md P6.4d-2).
 *
 * The workbench store mirrors the container's user files and assets in the tab's own memory,
 * so several tabs never share this state. Template files the store does not track are the ones
 * mounted at boot. When the store is trusted to mirror the container, a switch writes only the
 * files that differ and keeps Vite running; anything uncertain restores every file and restarts
 * it, as before.
 *
 * Pure, so it is unit-tested in node.
 */

export type SnapshotSwitchReason =
  | 'fast'
  /** The store is not known to mirror the container (first restore, reset, aborted switch). */
  | 'untrusted'
  | 'dev-server-down'
  | 'dependencies'
  /** A file Vite reads at startup changed. */
  | 'config'
  | 'too-many-changes'
  /** A fast switch did not leave exactly the version behind. */
  | 'mismatch';

/** Above this many written plus removed files, restarting Vite is the safer and faster path. */
export const FAST_SWITCH_MAX_CHANGES = 40;

const DEPENDENCY_FILES: ReadonlySet<string> = new Set(['package.json', 'package-lock.json']);

/** Files Vite reads when it starts, or that change what it serves; a change restarts it. */
const RESTART_FILE =
  /^(?:vite\.config\.[cm]?[jt]s|index\.html|postcss\.config\.[cm]?[jt]s|tailwind\.config\.[cm]?[jt]s|tsconfig(?:\.[\w-]+)?\.json|\.env(?:\.[\w-]+)?)$/;

export interface SnapshotPlanEntry {
  path: string;
  isBinary: boolean;
  /** Text files. */
  text?: string;
  /** Binary files (assets). */
  base64?: string;
}

export interface SnapshotWorkspaceState {
  entries: ReadonlyArray<SnapshotPlanEntry>;
  currentFiles: Readonly<Record<string, { content: string; modified?: boolean } | undefined>>;
  currentAssets: Readonly<Record<string, { base64: string } | undefined>>;
  /** Template files mounted at boot. */
  templateFiles: Readonly<Record<string, string>>;
  /** Written by the workbench itself (the preview bridge); never removed or compared. */
  protectedPaths: ReadonlySet<string>;
}

export interface SnapshotSwitchPlan {
  /** In the version and new or different in the container. */
  changedPaths: string[];
  /** Tracked in the container and not in the version (template and protected files excepted). */
  removedPaths: string[];
  /** Changed or removed files that need a Vite restart. */
  restartPaths: string[];
  dependenciesChanged: boolean;
}

function holdsEntry(entry: SnapshotPlanEntry, state: SnapshotWorkspaceState): boolean {
  if (entry.isBinary) return state.currentAssets[entry.path]?.base64 === entry.base64;
  const tracked = state.currentFiles[entry.path];
  if (tracked) return tracked.modified !== true && tracked.content === entry.text;
  return state.templateFiles[entry.path] === entry.text;
}

function trackedPaths(state: SnapshotWorkspaceState): string[] {
  return [...new Set([...Object.keys(state.currentFiles), ...Object.keys(state.currentAssets)])];
}

function isStale(
  path: string,
  target: ReadonlySet<string>,
  state: SnapshotWorkspaceState
): boolean {
  return !target.has(path) && !state.protectedPaths.has(path) && !(path in state.templateFiles);
}

export function planSnapshotSwitch(state: SnapshotWorkspaceState): SnapshotSwitchPlan {
  const target = new Set(state.entries.map((entry) => entry.path));
  const changedPaths = state.entries
    .filter((entry) => !holdsEntry(entry, state))
    .map((entry) => entry.path);
  const removedPaths = trackedPaths(state)
    .filter((path) => isStale(path, target, state))
    .sort();
  const restartPaths = [...changedPaths, ...removedPaths].filter(
    (path) => RESTART_FILE.test(path) || DEPENDENCY_FILES.has(path)
  );
  return {
    changedPaths,
    removedPaths,
    restartPaths,
    dependenciesChanged: restartPaths.some((path) => DEPENDENCY_FILES.has(path)),
  };
}

export function chooseSnapshotSwitchMode(
  plan: SnapshotSwitchPlan,
  context: { trusted: boolean; devServerReady: boolean; maxChanges?: number }
): { mode: 'fast' | 'restart'; reason: SnapshotSwitchReason } {
  if (!context.trusted) return { mode: 'restart', reason: 'untrusted' };
  if (!context.devServerReady) return { mode: 'restart', reason: 'dev-server-down' };
  if (plan.dependenciesChanged) return { mode: 'restart', reason: 'dependencies' };
  if (plan.restartPaths.length > 0) return { mode: 'restart', reason: 'config' };
  const changes = plan.changedPaths.length + plan.removedPaths.length;
  if (changes > (context.maxChanges ?? FAST_SWITCH_MAX_CHANGES)) {
    return { mode: 'restart', reason: 'too-many-changes' };
  }
  return { mode: 'fast', reason: 'fast' };
}

/**
 * Paths where the container does not hold exactly the version after a restore; empty when it
 * does. The preview bridge files and `index.html` are skipped: the workbench patches them after
 * every restore.
 */
export function findSnapshotMismatches(state: SnapshotWorkspaceState): string[] {
  const target = new Set(state.entries.map((entry) => entry.path));
  const mismatches = state.entries
    .filter(
      (entry) =>
        !state.protectedPaths.has(entry.path) &&
        entry.path !== 'index.html' &&
        !holdsEntry(entry, state)
    )
    .map((entry) => entry.path);
  for (const path of trackedPaths(state)) {
    if (isStale(path, target, state) && path !== 'index.html') mismatches.push(path);
  }
  return mismatches;
}
